import { Pipe, PipeTransform } from '@angular/core';
import { ItemProperties, ItemResource, PropertyProfile, ResourceMeta } from '@ngx-hal/models';

@Pipe({
  name: 'propertyResourceMeta',
  standalone: false,
})
export class PropertyResourceMetaPipe implements PipeTransform {
  private propertyResourceMeta?: ResourceMeta<never>;

  /**
   * Transforms a given property profile into a `ResourceMeta` object for the specified entity and property types.
   *
   * If the `propertyResourceMeta` is not already set, it constructs the meta information using the `alps.rt` property
   * from the provided `propertyProfile`. The method extracts the base href, and creates meta links for `link`, `profile`,
   * and `items` (with `/profile/` replaced by `/` in the href for `items`). If the href cannot be determined, it returns `undefined`.
   *
   * @typeParam Entity - The type of the resource entity, extending `ItemResource`.
   * @typeParam Property - The type of the property, extending `ItemProperties<Entity>`.
   * @param propertyProfile - The profile object describing the property for which meta information is to be generated.
   * @returns The constructed `ResourceMeta` object for the entity, or `undefined` if meta information cannot be determined.
   */
  transform<Entity extends ItemResource, Property extends ItemProperties<Entity>>(
    propertyProfile: PropertyProfile<Entity, Property>,
  ): ResourceMeta<Entity> | undefined {
    if (!this.propertyResourceMeta) {
      const href = propertyProfile.alps?.rt?.replace(/#[^/]*$/, '');
      this.propertyResourceMeta = href
        ? {
            link: { href },
            profile: { href },
            items: { href: href.replace(/\/profile\//, '/') },
          }
        : undefined;
    }

    return this.propertyResourceMeta as ResourceMeta<Entity> | undefined;
  }
}
